import { useEffect, useState } from "react";
import "./PlantationTwoTestimonials.scss";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import AddTestimonials from "../../../../default-hooks/addTestimonials";
import UpdateTestimonial from "../../../../default-hooks/UpdateTestimonial";
import firebase from "firebase/app";
import "firebase/storage";
import PGLoaders from "../../../../default-pages/PGLoaders/PGLoaders";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const PlantationTwoTestimonials = () => {
  const { user } = useAuthContext();
  const { documents: testimonials, error: testimonialsError } =
    useCollection("testimonials");
  const { documents: allUsers, error: usersError } = useCollection("users");
  const { document: dbAppTypeDocuments } = useDocument("settings", "AppType");

  const { deleteDocument } = useFirestore("testimonials");

  const [current, setCurrent] = useState(0);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [userMap, setUserMap] = useState({});
  const [deletePopup, setDeletePopup] = useState(false);
  const [toDeleteId, setToDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user && user.role === "superAdmin";

  useEffect(() => {
    if (allUsers && allUsers.length > 0) {
      const map = {};
      allUsers.forEach((u) => {
        map[u.id] = u;
      });
      setUserMap(map);
    }
  }, [allUsers]);

  const activeTestimonials = Array.isArray(testimonials)
    ? testimonials.filter((t) => (isAdmin ? true : t.status === "active"))
    : [];

  useEffect(() => {
    if (
      activeTestimonials.length === 0 ||
      current >= activeTestimonials.length
    ) {
      setCurrent(0);
    }
  }, [activeTestimonials, current]);

  useEffect(() => {
    if (activeTestimonials.length === 0) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % activeTestimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTestimonials]);

  const handleDeleteConfirm = async () => {
    if (!toDeleteId) return;
    setDeleting(true);
    try {
      const folderPath = `testimonialImages/${toDeleteId}`;
      const storageRef = firebase.storage().ref(folderPath);
      const listResult = await storageRef.listAll();

      if (listResult.items.length > 0) {
        const deletePromises = listResult.items.map((itemRef) =>
          itemRef.delete()
        );
        await Promise.all(deletePromises);
      }

      await deleteDocument(toDeleteId);

      setDeletePopup(false);
      setToDeleteId(null);
      setCurrent(0);
    } catch (err) {
      console.error("Error deleting testimonial and storage files:", err);
      alert(
        "Failed to delete testimonial and associated files. Check console."
      );
    }
    setDeleting(false);
  };

  const openDeletePopup = (id) => {
    setToDeleteId(id);
    setDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setToDeleteId(null);
  };

  if (testimonialsError || usersError)
    return <div className="error-message">Error loading data</div>;

  if (!testimonials || !allUsers)
    return (
      <div className="loading-message">
        <PGLoaders />
      </div>
    );

  if (activeTestimonials.length === 0)
    return (
      <section className="eco-testimonial-wrapper">
        <div className="eco-testimonial-text-side">
          <p>No active testimonials available yet.</p>
          {isAdmin && <AddTestimonials />}
        </div>
      </section>
    );

  const testimonial = activeTestimonials[current];
  if (!testimonial)
    return (
      <section className="eco-testimonial-wrapper">
        <div className="eco-testimonial-text-side">
          <p>No testimonials available to display.</p>
          {isAdmin && <AddTestimonials />}
        </div>
      </section>
    );

  const testimonialUser = userMap[testimonial.createdBy] || {};
  const overlayImg = testimonialUser.photoURL || testimonial.overlayImg || null;

  return (
    <section className="eco-testimonial-wrapper">
      <div className="eco-testimonial-image-side">
        <div className="img-main">
          <img
            src={testimonial.mainImg || "https://via.placeholder.com/400x300"}
            alt="Testimonial"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        </div>

        {/* ✅ Overlay only if user uploaded image */}
        {overlayImg && (
          <div className="img-overlay">
            <img
              src={overlayImg}
              alt={testimonial.name || "User"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 0,
                backgroundColor: "#eee",
              }}
            />
          </div>
        )}
      </div>

      <div className="eco-testimonial-text-side">
        <div className="testimonial-quote">
          <span className="quote-icon" aria-hidden="true">
            ❝
          </span>
          <p>{testimonial.description}</p>

          <div
            className="stars"
            aria-label={`Rating: ${testimonial.rating || 0} out of 5`}
          >
            {"★".repeat(Math.round(testimonial.rating || 0))}
            {"☆".repeat(5 - Math.round(testimonial.rating || 0))}
          </div>

          <h5>{testimonial.name || "Anonymous"}</h5>

          {testimonial.role && (
            <p
              style={{
                marginTop: "0.2rem",
                color: "gray",
                fontSize: "0.95rem",
              }}
            >
              {testimonial.role}
            </p>
          )}

          <div className="admin-actions" style={{ marginTop: "0.8rem" }}>
            {dbAppTypeDocuments && (
              <>
                {dbAppTypeDocuments.apptype === "lite" && isSuperAdmin && (
                  <EditIcon
                    className="edit-icon"
                    onClick={() => setEditingTestimonial(testimonial)}
                    style={{ cursor: "pointer", marginRight: "10px" }}
                    title="Edit Testimonial"
                    role="button"
                    tabIndex={0}
                  />
                )}

                {dbAppTypeDocuments.apptype === "pro" &&
                  (isSuperAdmin || isAdmin) && (
                    <EditIcon
                      className="edit-icon"
                      onClick={() => setEditingTestimonial(testimonial)}
                      style={{ cursor: "pointer", marginRight: "10px" }}
                      title="Edit Testimonial"
                      role="button"
                      tabIndex={0}
                    />
                  )}
              </>
            )}

            {dbAppTypeDocuments && (
              <>
                {dbAppTypeDocuments.apptype === "lite" && isSuperAdmin && (
                  <DeleteIcon
                    onClick={() => openDeletePopup(testimonial.id)}
                    style={{ cursor: "pointer" }}
                    title="Delete Testimonial"
                    role="button"
                    tabIndex={0}
                  />
                )}

                {dbAppTypeDocuments.apptype === "pro" && isSuperAdmin && (
                  <DeleteIcon
                    onClick={() => openDeletePopup(testimonial.id)}
                    style={{ cursor: "pointer" }}
                    title="Delete Testimonial"
                    role="button"
                    tabIndex={0}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div
          className="testimonial-dots"
          role="tablist"
          aria-label="Testimonial navigation dots"
        >
          {activeTestimonials.map((_, i) => (
            <span
              key={i}
              className={i === current ? "dot active" : "dot"}
              onClick={() => setCurrent(i)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setCurrent(i);
                }
              }}
              aria-selected={i === current}
              role="tab"
              tabIndex={0}
              aria-label={`Go to testimonial ${i + 1}`}
            />
          ))}
        </div>

        {dbAppTypeDocuments && (
          <>
            {dbAppTypeDocuments.apptype === "lite" && isSuperAdmin && (
              <AddTestimonials />
            )}

            {dbAppTypeDocuments.apptype === "pro" &&
              (isSuperAdmin || isAdmin) && <AddTestimonials />}
          </>
        )}

        {editingTestimonial && (
          <UpdateTestimonial
            testimonial={editingTestimonial}
            onClose={() => setEditingTestimonial(null)}
          />
        )}
      </div>

      {deletePopup && (
        <div className="delete-confirm-overlay" role="dialog" aria-modal="true">
          <div className="delete-confirm-modal">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this testimonial?</p>
            <div style={{ marginTop: "1rem" }}>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{
                  backgroundColor: "#b00020",
                  color: "white",
                  marginRight: "1rem",
                }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
              <button onClick={closeDeletePopup}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlantationTwoTestimonials;
